import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.css';
import lightbulbIcon from 'V2/Shared/Assets/icon-lightbulb.svg';
import notificationWarningIcon from 'V2/Shared/Assets/icon-notification-important.svg';
import notificationInfoIcon from 'V2/Shared/Assets/icon-information-gray.svg';
import { Nullable } from 'V2/Types';

type DisclaimerPropsType = {
  children: string | React.ReactNode;
  type?: string;
  subtype?: Nullable<string>; // {{icon_name}}_{{disclaimer_subtype}}
  className?: string;
};

type BaseDisclaimerProps = {
  children: string | React.ReactNode;
  className?: string;
};

const DISCLAIMER_ICON_NAMES = {
  notification_warning: notificationWarningIcon,
  notification_info: notificationInfoIcon,
  lightbulb: lightbulbIcon,
};
type DisclaimerIconName = keyof typeof DISCLAIMER_ICON_NAMES;

const isValidIconName = (iconName: string): iconName is DisclaimerIconName =>
  iconName in DISCLAIMER_ICON_NAMES;
const isDisclaimerStyled = (subtype?: Nullable<string>): boolean => !!subtype;

const getDisclaimerIcon = (disclaimerSubtype?: Nullable<string>) => {
  if (!disclaimerSubtype) return undefined;
  if (!isValidIconName(disclaimerSubtype)) return undefined;

  return DISCLAIMER_ICON_NAMES[disclaimerSubtype];
};

export const DisclaimerFactory: React.FC<DisclaimerPropsType> = (props) => {
  const { type, subtype } = props;
  if (type === 'disclaimer' && isDisclaimerStyled(subtype)) {
    return <DisclaimerStyled {...props} />;
  }
  return <Disclaimer {...props} />;
};

export const DisclaimerStyled: React.FC<
  BaseDisclaimerProps & {
    subtype?: Nullable<string>;
  }
> = ({ children, subtype, className }) => (
  <div
    className={classNames(styles.main, className, {
      [styles.warning]: subtype === 'notification_warning',
      [styles.info]: subtype === 'notification_info',
    })}
  >
    <img
      src={getDisclaimerIcon(subtype)}
      alt="Disclaimer"
      data-testid="disclaimer"
    />
    <div>{children}</div>
  </div>
);

export const Disclaimer: React.FC<
  BaseDisclaimerProps & {
    type?: string;
  }
> = ({ children, type, className }) => (
  <div
    className={classNames(styles.main, className, {
      [styles.center]: type === 'disclaimer',
    })}
  >
    {type !== 'disclaimer' && (
      <img src={lightbulbIcon} alt="Disclaimer" data-testid="disclaimer" />
    )}
    <div>{children}</div>
  </div>
);
